import { useEffect, useMemo, useState } from "react";
import { Cpu, Radio } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import {
  type Device,
  type Sensor,
  createSensorTag,
  listDevices,
  getDevice,
} from "../../lib/instrumentationApi";

interface PlaceSensorTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  virtualSiteId: string;
  /** Exactly one of pointCloudId / digitalTwinId must be set; the modal
   *  forwards the right field to the API. */
  pointCloudId?: string;
  digitalTwinId?: string;
  worldPoint: { x: number; y: number; z: number } | null;
  /** Sensor IDs that already have a tag on this container — hidden from the
   *  picker so the user can't 409 themselves on the unique constraint. */
  taggedSensorIds?: ReadonlyArray<string>;
  onCreated: () => void;
}

/** Shared modal for both DigitalTwinViewer and PointCloudViewer. The user
 *  picks a device, then a sensor on that device, sets an optional label
 *  and color, and the tag is persisted with a {x, y, z} geometry. */
export function PlaceSensorTagModal({
  isOpen,
  onClose,
  virtualSiteId,
  pointCloudId,
  digitalTwinId,
  worldPoint,
  taggedSensorIds = [],
  onCreated,
}: PlaceSensorTagModalProps) {
  const [devices, setDevices] = useState<Device[]>([]);
  const [deviceId, setDeviceId] = useState<string>("");
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [sensorId, setSensorId] = useState<string>("");
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("#22d3ee");
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [isLoadingSensors, setIsLoadingSensors] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setIsLoadingDevices(true);
    listDevices({ virtual_site_id: virtualSiteId, page_size: 100 })
      .then((res) => {
        if (!cancelled) setDevices(res.items);
      })
      .catch(() => {
        if (!cancelled) setDevices([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDevices(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, virtualSiteId]);

  // Whenever the user picks a device, pull its sensors so the second
  // dropdown can populate. Cleared when the device changes so the previous
  // device's sensors don't linger.
  useEffect(() => {
    if (!deviceId) {
      setSensors([]);
      setSensorId("");
      return;
    }
    let cancelled = false;
    setIsLoadingSensors(true);
    setSensorId("");
    getDevice(deviceId)
      .then((d) => {
        if (!cancelled) setSensors(d.sensors || []);
      })
      .catch(() => {
        if (!cancelled) setSensors([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSensors(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId]);

  const taken = useMemo(() => new Set(taggedSensorIds), [taggedSensorIds]);
  const availableSensors = useMemo(
    () => sensors.filter((s) => !taken.has(s.id)),
    [sensors, taken],
  );

  function reset() {
    setDeviceId("");
    setSensorId("");
    setLabel("");
    setColor("#22d3ee");
    setSensors([]);
    setError(null);
  }

  function handleClose() {
    if (isCreating) return;
    reset();
    onClose();
  }

  async function handleCreate() {
    if (!worldPoint || !sensorId) {
      setError("Pick a device and a sensor first.");
      return;
    }
    setIsCreating(true);
    setError(null);
    try {
      await createSensorTag({
        sensor_id: sensorId,
        point_cloud_id: pointCloudId ?? null,
        digital_twin_id: digitalTwinId ?? null,
        geometry: { x: worldPoint.x, y: worldPoint.y, z: worldPoint.z },
        label: label || undefined,
        color,
      });
      onCreated();
      reset();
      onClose();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail || "Failed to place sensor tag");
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Place Sensor Tag" size="md">
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {worldPoint && (
          <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 px-4 py-3 text-xs text-cyan-200">
            <div className="flex items-center gap-2">
              <Radio className="h-3.5 w-3.5" />
              <span className="font-medium">3D Position</span>
            </div>
            <div className="mt-1 font-mono text-cyan-100/90">
              x: {worldPoint.x.toFixed(3)} · y: {worldPoint.y.toFixed(3)} · z: {worldPoint.z.toFixed(3)}
            </div>
          </div>
        )}

        <div>
          <label className="mb-1.5 block text-sm font-medium text-surface-200">
            Device <span className="text-red-400">*</span>
          </label>
          <select
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            disabled={isLoadingDevices || isCreating}
            className="w-full rounded-lg border border-surface-700 bg-surface-900 px-3 py-2 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">
              {isLoadingDevices
                ? "Loading devices…"
                : devices.length === 0
                  ? "No devices in this site — add one on the Instrumentation page first"
                  : "Select a device"}
            </option>
            {devices.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
                {d.source_type ? ` · ${d.source_type}` : ""}
                {d.sensor_count ? ` · ${d.sensor_count} sensor${d.sensor_count === 1 ? "" : "s"}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-surface-200">
            Sensor <span className="text-red-400">*</span>
          </label>
          <select
            value={sensorId}
            onChange={(e) => setSensorId(e.target.value)}
            disabled={!deviceId || isLoadingSensors || isCreating}
            className="w-full rounded-lg border border-surface-700 bg-surface-900 px-3 py-2 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">
              {!deviceId
                ? "Pick a device first"
                : isLoadingSensors
                  ? "Loading sensors…"
                  : availableSensors.length === 0
                    ? sensors.length === 0
                      ? "This device has no sensors yet"
                      : "All sensors on this device are already tagged here"
                    : "Select a sensor"}
            </option>
            {availableSensors.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.sensor_type ? ` · ${s.sensor_type}` : ""}
                {s.unit ? ` (${s.unit})` : ""}
              </option>
            ))}
          </select>
          {taken.size > 0 && (
            <p className="mt-1 text-[11px] text-surface-500">
              {taken.size} sensor{taken.size === 1 ? "" : "s"} already tagged here — hidden from the list above.
            </p>
          )}
        </div>

        <Input
          label="Label"
          placeholder="Optional, e.g. Pump-A bearing vibration"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={isCreating}
        />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-surface-200">Color</label>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            disabled={isCreating}
            className="h-10 w-20 rounded-lg border border-surface-700 bg-surface-900"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={handleClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} isLoading={isCreating} disabled={!sensorId || !worldPoint}>
            <Cpu className="mr-1.5 h-4 w-4" />
            Place sensor tag
          </Button>
        </div>
      </div>
    </Modal>
  );
}
